import { createContext, ReactNode, useContext, useMemo } from 'react'
import { usePartialSave } from '@/hooks/usePartialSave'

export interface EmbedState {
  isEmbedded: boolean
  showQuitButton: boolean
  onExit: (reason: string) => void
  onPageChange: () => void
}

const defaultEmbedState: EmbedState = {
  isEmbedded: false,
  showQuitButton: false,
  onExit: () => {},
  onPageChange: () => {}
}

export const EmbedContext = createContext<EmbedState>(defaultEmbedState)

export function hasQuitButtonParam(src?: string): boolean {
  const { searchParams } = new URL(src ?? document.location.href)

  return searchParams.has('quit-button')
}

interface EmbedProviderProps {
  children: ReactNode
  questionnaireId: string
}

export function EmbedProvider({
  children,
  questionnaireId
}: EmbedProviderProps) {
  const { clearPartialSaveData } = usePartialSave(questionnaireId)

  const value = useMemo<EmbedState>(
    () => ({
      isEmbedded: true,
      showQuitButton: hasQuitButtonParam(),
      onExit: (reason: string) => {
        clearPartialSaveData()
        window.parent.postMessage(
          { type: 'exit', reason },
          '*' // allowing any target domain because we don't send sensitive data
        )
      },
      onPageChange: () => {
        window.parent.postMessage(
          { type: 'change-page' },
          '*' // allowing any target domain because we don't send sensitive data
        )
      }
    }),
    [clearPartialSaveData]
  )

  return <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>
}

export const useEmbed = () => useContext(EmbedContext)
